import type { Theme, SxProps } from '@mui/material/styles'
import { colors } from '../theme'

/**
 * Shared MUI table styling, applied as `sx` by every list table in the app.
 *
 * This file, not the theme, is what a table actually looks like: `sx` beats
 * `styleOverrides`, so the sizes here win over MuiTableCell. Anything the
 * design system says about tables has to be said here as well, or it will
 * silently not apply.
 *
 * Separation is a 1px hairline on gray200 around the container and gray100
 * between rows, with no shadow — the revamp replaced Material elevation with
 * borders. Hover and selection are steps along the same grey ramp rather than
 * a tint, so a row never signals state by colour alone.
 *
 * The zebra banding this file used to carry was already inert: it read two
 * "surface container" values that Missfit's theme does not define, so both
 * fell back to background.paper and every row painted the same white. It is
 * gone rather than fixed, because rows are separated by rules now.
 */
export const getEnhancedTableStyles = (
  theme: Theme
): { tableContainer: SxProps<Theme>; table: SxProps<Theme> } => {
  return {
    tableContainer: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: '10px',
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
    },

    table: {
      backgroundColor: theme.palette.background.paper,

      '& .MuiTableHead-root': {
        '& .MuiTableCell-root': {
          backgroundColor: colors.gray50,
          fontWeight: 600,
          fontSize: '0.75rem',
          lineHeight: '1rem',
          color: theme.palette.text.secondary,
          padding: '0 14px',
          height: 36,
          // The head row is a fixed 36px, so a two-word label that wraps gets
          // squeezed rather than given a second line. From md up there is room
          // to keep every label on one line; below it, wrapping is still the
          // better trade against a horizontally scrolling header.
          whiteSpace: 'normal',
          [theme.breakpoints.up('md')]: {
            whiteSpace: 'nowrap',
          },
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 4,
        },
      },

      '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
          '&:hover': {
            backgroundColor: colors.gray50,
            cursor: 'pointer',
          },

          '&.Mui-selected': {
            backgroundColor: colors.gray100,
            '&:hover': {
              backgroundColor: colors.gray100,
            },
          },

          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shortest,
          }),
        },

        '& .MuiTableCell-root': {
          padding: '13px 14px',
          fontSize: '0.8125rem',
          lineHeight: '1.125rem',
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${colors.gray100}`,
        },
      },
    },
  }
}
